import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FileDown, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "@/lib/api";
import { fmtDate, STATUSES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { RiderFormDialog } from "@/components/RiderFormDialog";
import { Avatar } from "@/components/Avatar";
import { Input } from "@/components/ui/input";
import { DocApprovals } from "@/components/RiderDocs";

const FILTERS = ["", "current", "due_soon", "overdue", "awaiting", "referred", "unsuccessful"];

export default function Riders() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const [q, setQ] = useState("");
  const [riders, setRiders] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/riders", { params: { status: status || undefined, q: q || undefined } }).then((r) => setRiders(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [status, q]);

  const canEdit = ["admin", "assessor"].includes(user.role);

  return (
    <div className="space-y-8">
      {(user.permissions?.riders || 0) >= 2 && <DocApprovals />}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Members</p>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">Riders</h1>
        </div>
        <div className="flex flex-wrap gap-2">
        {["admin", "trustee"].includes(user.role) && (
          <button data-testid="riders-pdf-btn" onClick={() => downloadFile("/reports/riders.pdf", "SSCBB-riders-summary.pdf").catch((e) => toast.error(errMsg(e)))} className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold hover:bg-secondary">
            <FileDown className="h-4 w-4" /> Summary PDF
          </button>
        )}
        {canEdit && (
          <button data-testid="add-rider-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground">
            <Plus className="h-4 w-4" /> Add rider
          </button>
        )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search name, membership no, email" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-9" data-testid="rider-search-input" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {FILTERS.map((s) => (
            <button key={s || "all"} data-testid={`rider-filter-${s || "all"}`} onClick={() => setParams(s ? { status: s } : {})}
              className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${status === s ? "border-primary bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {s ? STATUSES[s].label : "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border bg-card" data-testid="riders-list">
        <div className="hidden grid-cols-12 gap-4 border-b px-5 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground md:grid">
          <div className="col-span-4">Rider</div><div className="col-span-2">Qualification</div><div className="col-span-2">Last assessed</div><div className="col-span-2">Next due</div><div className="col-span-2">Status</div>
        </div>
        {riders === null && <div className="h-24 animate-pulse" />}
        {riders?.length === 0 && <p className="px-5 py-10 text-sm text-muted-foreground">No riders found.</p>}
        {riders?.map((r) => (
          <Link key={r.id} to={`/riders/${r.id}`} data-testid={`rider-row-${r.id}`} className="grid grid-cols-2 items-center gap-2 border-b px-5 py-4 last:border-0 hover:bg-secondary/50 md:grid-cols-12 md:gap-4">
            <div className="flex items-center gap-3 md:col-span-4">
              <Avatar photo={r.photo} name={`${r.first_name} ${r.last_name}`} testid={`rider-row-avatar-${r.id}`} />
              <div className="min-w-0">
              <div className="font-semibold">{r.first_name} {r.last_name}</div>
              <div className="text-xs text-muted-foreground">{r.membership_number || "No membership no."} · {r.member_type === "new" ? "New member" : "Existing"}</div>
              </div>
            </div>
            <div className="justify-self-end md:hidden"><StatusBadge status={r.status} />{r.docs_needed > 0 && <span className="ml-1 rounded-full bg-red-600/15 px-2 py-0.5 text-[11px] font-bold text-red-500">Docs needed</span>}</div>
            <div className="hidden text-sm md:col-span-2 md:block">{r.qualification || "—"}</div>
            <div className="text-xs text-muted-foreground md:col-span-2 md:text-sm md:text-foreground"><span className="md:hidden">Last: </span>{fmtDate(r.last_assessment_date)}</div>
            <div className="justify-self-end text-xs text-muted-foreground md:col-span-2 md:justify-self-start md:text-sm md:text-foreground"><span className="md:hidden">Due: </span><span className="font-mono">{fmtDate(r.next_due_date)}</span></div>
            <div className="hidden md:col-span-2 md:block"><StatusBadge status={r.status} />{r.docs_needed > 0 && <span className="mt-1 block w-fit rounded-full bg-red-600/15 px-2 py-0.5 text-[11px] font-bold text-red-500" data-testid={`rider-docs-needed-${r.id}`}>Docs needed</span>}</div>
          </Link>
        ))}
      </div>
      {open && <RiderFormDialog open={open} onOpenChange={setOpen} onSaved={(r) => setRiders((x) => [r, ...(x || [])])} />}
    </div>
  );
}
